import { CustomListKey, displayName, formatDateNoTime, User } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import type { inferRouterOutputs } from "@trpc/server";
import { useTaskNouns } from "hooks/use-task-nouns";
import * as React from "react";
import { useHistory } from "react-router";
import { useCustomList } from "store/selectors/bridge";
import { useCrmMode } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import { CrmMode } from "util/crm-mode";
import { ClientPersonaLink } from "./list-client/client-persona-link";

export type ReferredClientRow =
    inferRouterOutputs<AppRouter>["referralSource"]["listReferredClients"][number];

interface Props {
    clients: ReferredClientRow[];
    users: { [id: string]: User };
}

const moveInStartLabel = (mode: CrmMode): string => {
    if (mode === "placement-only") {
        return "Move-in";
    }
    if (mode === "home-care-only") {
        return "Start";
    }
    return "Move-in / Start";
};

const keyDateClassificationLabel = (classification: "actual" | "estimated"): string =>
    classification === "actual" ? "Actual" : "Estimated";

export const ListReferredClients: React.FC<Props> = ({ clients, users }) => {
    const nouns = useTaskNouns();
    const history = useHistory();
    const { mode } = useCrmMode();
    const { settings } = useTenantSettings();
    const showClientSerial = settings.showClientSerial;
    const placementStatus = useCustomList(CustomListKey.ClientStatus);
    const homeCareLeadStatus = useCustomList(CustomListKey.HomeCareLeadStatus);

    const columnHeaders = [
        ...(showClientSerial ? ["ID"] : []),
        "Client",
        "Date Created",
        "Status",
        `Next ${nouns.titleSingular}`,
        moveInStartLabel(mode),
        "Assigned to",
    ];

    const statusName = (row: ReferredClientRow): string => {
        const list = row.type === "placement" ? placementStatus : homeCareLeadStatus;
        return list.getListItemName(row.statusListItemId);
    };

    const linkFor = (row: ReferredClientRow): string =>
        row.type === "placement"
            ? `/clients/show/${row.engagementId}`
            : `/home-care-clients/show/${row.engagementId}`;

    const assignedName = (assignedUserId: string | null | undefined): string =>
        assignedUserId && users[assignedUserId]
            ? displayName(users[assignedUserId])
            : "unassigned";

    return (
        <div className="shadow overflow-x-auto border-b border-gray-200 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        {columnHeaders.map(header => (
                            <th
                                key={header}
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                {header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {clients.map(row => (
                        <tr
                            key={row.engagementId}
                            onClick={() => history.push(linkFor(row))}
                            className="hover:bg-gray-100 cursor-pointer">
                            {showClientSerial ? (
                                <td className="px-6 py-4 truncate max-w-xs">
                                    {row.serial ?? ""}
                                </td>
                            ) : null}
                            <td className="px-6 py-4 truncate max-w-xs">
                                <ClientPersonaLink
                                    client={{
                                        appLink: linkFor(row),
                                        name: row.name,
                                        bestContactName: row.bestContactName,
                                    }}
                                />
                            </td>
                            <td className="px-6 py-4 truncate max-w-xs">
                                {formatDateNoTime(row.dateAdded)}
                            </td>
                            <td className="px-6 py-4 truncate max-w-xs">
                                {statusName(row)}
                            </td>
                            <td className="px-6 py-4 truncate max-w-xs">
                                <div className="text-sm truncate text-gray-900">
                                    {row.nextTask?.text}
                                </div>
                                <div className="text-sm text-gray-500">
                                    {row.nextTask?.dueDate
                                        ? formatDateNoTime(row.nextTask.dueDate)
                                        : null}
                                </div>
                            </td>
                            <td className="px-6 py-4 truncate max-w-xs">
                                {row.keyDate ? (
                                    <div>
                                        <div className="text-sm text-gray-900">
                                            {formatDateNoTime(row.keyDate.date)}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            {keyDateClassificationLabel(
                                                row.keyDate.classification,
                                            )}
                                        </div>
                                    </div>
                                ) : null}
                            </td>
                            <td className="px-6 py-4 truncate max-w-xs">
                                {assignedName(row.assignedUserId)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
